import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { APITypes } from '../../primary-record-types';
import urls from '../../utils/urls';
import useNotify from '../useNotify';
import { getIntl } from '../../utils/intl';
import { appAPI } from '../../utils/api';
import {
    getOneUpHealthEndpointDetailsCacheKey,
    getOneUpHealthEndpointIngestionsCacheKey,
    getProfileConnectedHealthSystemsCacheKey,
    onDeletePortalConnection,
    onPatientMismatchConfirmedUpdate,
    onResetConnectedHealthSystems,
    onResourceUpdate,
    onTogglePortalAutomaticRefresh,
} from './cacheKeys';
import { useQueryErrorNotify } from '../useQueryCallbacks';
import { useHasPermission } from '../../components/v2BaseComponents/Permissions/hooks';

const intl = getIntl('useProfileConnectedHealthSystems', {
    errorSearching: 'Error loading connected health systems',
    successDisconnecting:
        'Successfully disconnected patient portal.  It may take a few minutes for the data to refresh.',
    refreshedDataFromPortalDisconnect: 'Refreshed data from portal disconnection',
    errorDisconnecting: 'Error disconnecting patient portal',
    connectionDetailsError: 'Error loading connection details',
    ingestionHistoryError: 'Error loading connection history',
    confirmPatientMismatchError: 'Error confirming patient',
    confirmPatientMismatchSuccess: 'Successfully confirmed patient',
    errorUpdatingAutomaticRefresh: 'Error updating automatic refresh',
});

export type UseProfileConnectedHealthSystemsArgs = {
    medicalProfileId: string | null;
    onHealthSystemDisconnect?: () => void;
    healthSystemId?: string;
};

export type UseProfileConnectedHealthSystemsReturnValue = {
    isLoading: boolean;
    connectedHealthSystems: APITypes.ehrIntegrationsService.ConnectedHealthSystemResponseItem[];
    pendingDataHealthSystems: APITypes.ehrIntegrationsService.PendingConnection[];
    disconnectHealthSystem: (healthSystemId: string) => void;
    isDisconnectingHealthSystem: boolean;
    isLoadingHealthSystemConnectionDetails: boolean;
    healthSystemConnectionDetails: APITypes.ehrIntegrationsService.ConnectedHealthSystemDetails | null;
    isLoadingHealthSystemIngestionHistory: boolean;
    healthSystemIngestionHistory: APITypes.ehrIntegrationsService.OneUpDataPullHistoryResponse | null;
    confirmPatientMismatch: (healthSystemId: string) => void;
    isConfirmingPatientMismatch: boolean;
    updateAutomaticRefresh: (userDisabledAutomaticRefresh: boolean) => void;
    isUpdatingAutomaticRefresh: boolean;
};

export const useResetConnectedHealthSystems = () => {
    return (medicalProfileId: string) => {
        onResetConnectedHealthSystems.resetCache({ medicalProfileId })();
    };
};

export default function useProfileConnectedHealthSystems({
    medicalProfileId,
    onHealthSystemDisconnect,
    healthSystemId,
}: UseProfileConnectedHealthSystemsArgs): UseProfileConnectedHealthSystemsReturnValue {
    const queryClient = useQueryClient();
    const notify = useNotify();
    const hasViewPermission = useHasPermission(medicalProfileId, 'patientPortals', 'view');
    const profileConnectedHealthSystemsQuery = useQuery({
        queryKey: getProfileConnectedHealthSystemsCacheKey(medicalProfileId || ''),
        queryFn: async () => {
            return await appAPI.get(
                urls.api.ehrIntegrations.oneUpHealth.endpoints.connectedEndpoints(medicalProfileId!),
            );
        },
        enabled: hasViewPermission && !!medicalProfileId,
    });
    useQueryErrorNotify(profileConnectedHealthSystemsQuery, intl('errorSearching'));

    const disconnectHealthSystem = useMutation({
        mutationFn: (healthSystemId: string) => {
            if (!medicalProfileId) {
                throw new Error('Missing medicalProfileId');
            }

            return appAPI.del(
                urls.api.ehrIntegrations.oneUpHealth.endpoints.connectedEndpoint(medicalProfileId, healthSystemId),
            );
        },
        onError: () => {
            notify('error', intl('errorDisconnecting'));
        },
        onSuccess: () => {
            notify('success', intl('successDisconnecting'));
            onHealthSystemDisconnect?.();
            onDeletePortalConnection.resetCache({ medicalProfileId: medicalProfileId! })();
            setTimeout(() => {
                notify('success', intl('refreshedDataFromPortalDisconnect'));
                onResourceUpdate.resetCache({ medicalProfileId: medicalProfileId! })();
            }, 30 * 1000); // delay to allow data to clear
        },
    });

    const healthSystemConnectionDetailsQuery = useQuery({
        queryKey: getOneUpHealthEndpointDetailsCacheKey(medicalProfileId || '', healthSystemId || ''),
        queryFn: async () => {
            return await appAPI.get(
                urls.api.ehrIntegrations.oneUpHealth.endpoints.connectedEndpoint(
                    medicalProfileId!,
                    healthSystemId || '',
                ),
            );
        },
        enabled: !!healthSystemId && !!medicalProfileId,
    });
    useQueryErrorNotify(healthSystemConnectionDetailsQuery, intl('connectionDetailsError'));

    const healthSystemIngestionHistoryQuery = useQuery({
        queryKey: getOneUpHealthEndpointIngestionsCacheKey(medicalProfileId || '', healthSystemId || ''),

        queryFn: async () => {
            return await appAPI.get(
                urls.api.ehrIntegrations.oneUpHealth.endpoints.endpointIngestionHistory(
                    medicalProfileId!,
                    healthSystemId || '',
                ),
            );
        },
        enabled: !!healthSystemId && !!medicalProfileId,
    });
    useQueryErrorNotify(healthSystemIngestionHistoryQuery, intl('ingestionHistoryError'));

    const confirmPatientMismatch = useMutation({
        mutationFn: (healthSystemId: string) => {
            if (!medicalProfileId) {
                throw new Error('Missing medicalProfileId');
            }

            return appAPI.post(
                urls.api.ehrIntegrations.oneUpHealth.endpoints.confirmPatientMismatch(medicalProfileId, healthSystemId),
            );
        },
        onError: () => {
            notify('error', intl('confirmPatientMismatchError'));
        },
        onSuccess: (_data, args) => {
            notify('success', intl('confirmPatientMismatchSuccess'));
            onPatientMismatchConfirmedUpdate.resetCache({ medicalProfileId: medicalProfileId!, endpointId: args })();
        },
    });

    const updateAutomaticRefresh = useMutation({
        mutationFn: (userDisabledAutomaticRefresh: boolean) => {
            queryClient.setQueryData(
                getOneUpHealthEndpointDetailsCacheKey(medicalProfileId || '', healthSystemId || ''),
                (oldData: APITypes.ehrIntegrationsService.ConnectedHealthSystemDetails | null) => {
                    if (!oldData) {
                        return null;
                    } else {
                        return {
                            ...oldData,
                            userDisabledAutomaticRefresh,
                        };
                    }
                },
            );
            if (!medicalProfileId) {
                throw new Error('Missing medicalProfileId');
            }

            if (!healthSystemId) {
                throw new Error('Missing healthSystemId');
            }

            return appAPI.put(
                urls.api.ehrIntegrations.oneUpHealth.endpoints.automaticRefreshStatus(medicalProfileId, healthSystemId),
                {
                    body: { userDisabledAutomaticRefresh },
                },
            );
        },
        onError: () => {
            notify('error', intl('errorUpdatingAutomaticRefresh'));
        },
        onSuccess: () => {
            onTogglePortalAutomaticRefresh.resetCache({
                medicalProfileId: medicalProfileId!,
            })();
        },
    });

    return {
        isLoading: profileConnectedHealthSystemsQuery.isLoading,
        connectedHealthSystems: profileConnectedHealthSystemsQuery.data?.connectedHealthSystems || [],
        pendingDataHealthSystems: profileConnectedHealthSystemsQuery.data?.pendingConnections || [],
        isDisconnectingHealthSystem: disconnectHealthSystem.isPending,
        disconnectHealthSystem: (healthSystemId: string) => {
            disconnectHealthSystem.mutate(healthSystemId);
        },
        isLoadingHealthSystemConnectionDetails: healthSystemConnectionDetailsQuery.isLoading,
        healthSystemConnectionDetails: healthSystemConnectionDetailsQuery.data || null,
        isLoadingHealthSystemIngestionHistory: healthSystemIngestionHistoryQuery.isLoading,
        healthSystemIngestionHistory: healthSystemIngestionHistoryQuery.data || null,
        confirmPatientMismatch: confirmPatientMismatch.mutate,
        isConfirmingPatientMismatch: confirmPatientMismatch.isPending,
        isUpdatingAutomaticRefresh: updateAutomaticRefresh.isPending,
        updateAutomaticRefresh: updateAutomaticRefresh.mutate,
    };
}
